import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import CateringIcon from '@mui/icons-material/Fastfood';
import ViewListIcon from '@mui/icons-material/ViewList';
import CategoryIcon from '@mui/icons-material/Category';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

const SalesDetailsDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  glAccount,
  memoDetail,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showOrderGuid, setShowOrderGuid] = useState(false);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'individual'
  const [orderBy, setOrderBy] = useState('totalGross');
  const [order, setOrder] = useState('desc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  // Listen for copy events on the table
  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredDetails = useMemo(() => {
    if (!details.length) return details;
    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;
    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        const cellValue = String(row[columnId] ?? '').toLowerCase();
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  useEffect(() => {
    if (open && businessDate && storeId && glAccount) {
      fetchDetails();
    }
  }, [open, businessDate, storeId, glAccount]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);

      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const response = await axios.get('/hd-api/gl-export/sales-details', {
        params: {
          businessDate,
          storeId,
          glAccount,
        },
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
      });

      setDetails(response.data.data || []);
    } catch (err) {
      console.error('Error fetching sales details:', err);
      setError('Failed to load sales details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get category label based on GL account
  const getCategoryLabel = () => {
    switch (glAccount) {
      case '400050':
        return 'Food Sales';
      case '401000':
        return 'Catering Sales';
      case '401500':
        return 'Beer & Wine';
      default:
        return memoDetail || 'Sales Details';
    }
  };

  // Get icon for the category
  const getCategoryIcon = () => {
    switch (glAccount) {
      case '400050':
        return <RestaurantIcon color="action" />;
      case '401000':
        return <CateringIcon color="action" />;
      case '401500':
        return <LocalBarIcon color="action" />;
      default:
        return <RestaurantIcon color="action" />;
    }
  };

  // Group data by item name
  const groupedData = useMemo(() => {
    if (!filteredDetails.length) return [];

    const grouped = {};
    filteredDetails.forEach((item) => {
      const key = item.itemName || 'Unknown Item';
      if (!grouped[key]) {
        grouped[key] = {
          itemName: key,
          category: item.category,
          quantity: 0,
          totalGross: 0,
          totalNet: 0,
          totalDiscount: 0,
        };
      }
      grouped[key].quantity += 1;
      grouped[key].totalGross += parseFloat(item.grossSale) || 0;
      grouped[key].totalNet += parseFloat(item.netSale) || 0;
      grouped[key].totalDiscount += parseFloat(item.discountAmount) || 0;
    });

    return Object.values(grouped);
  }, [filteredDetails]);

  // Sort grouped data
  const sortedGroupedData = useMemo(() => {
    if (!groupedData.length) return groupedData;

    return [...groupedData].sort((a, b) => {
      let aVal, bVal;

      switch (orderBy) {
        case 'quantity':
          aVal = a.quantity;
          bVal = b.quantity;
          break;
        case 'grossSale':
        case 'totalGross':
          aVal = a.totalGross;
          bVal = b.totalGross;
          break;
        case 'netSale':
        case 'totalNet':
          aVal = a.totalNet;
          bVal = b.totalNet;
          break;
        case 'discountAmount':
        case 'totalDiscount':
          aVal = a.totalDiscount;
          bVal = b.totalDiscount;
          break;
        case 'itemName':
          aVal = a.itemName.toLowerCase();
          bVal = b.itemName.toLowerCase();
          break;
        default:
          aVal = a.totalGross;
          bVal = b.totalGross;
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [groupedData, orderBy, order]);

  // Sort individual data
  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      if (['grossSale', 'netSale', 'discountAmount', 'refundAmount', 'orderNumber'].includes(orderBy)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  // Calculate totals
  const totals = useMemo(() => {
    // For refunds, we need to deduplicate by orderGuid since refund is at order level
    const seenOrders = new Set();
    let totalRefund = 0;

    filteredDetails.forEach((d) => {
      if (!seenOrders.has(d.orderGuid)) {
        seenOrders.add(d.orderGuid);
        totalRefund += parseFloat(d.refundAmount) || 0;
      }
    });

    return filteredDetails.reduce(
      (acc, d) => ({
        grossSale: acc.grossSale + (parseFloat(d.grossSale) || 0),
        netSale: acc.netSale + (parseFloat(d.netSale) || 0),
        discount: acc.discount + (parseFloat(d.discountAmount) || 0),
        count: acc.count + 1,
      }),
      { grossSale: 0, netSale: 0, discount: 0, count: 0, refund: totalRefund }
    );
  }, [filteredDetails]);

  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Count unique items (from unfiltered data for comparison)
  const totalUniqueItemCount = useMemo(() => {
    const grouped = {};
    details.forEach((item) => {
      const key = item.itemName || 'Unknown Item';
      if (!grouped[key]) {
        grouped[key] = true;
      }
    });
    return Object.keys(grouped).length;
  }, [details]);

  // Count unique items (from filtered data)
  const uniqueItemCount = useMemo(() => {
    return groupedData.length;
  }, [groupedData]);

  // Export to Excel
  const handleExport = () => {
    const dataToExport = viewMode === 'grouped' ? sortedGroupedData : sortedDetails;

    const exportData = dataToExport.map((item) => {
      if (viewMode === 'grouped') {
        return {
          'Item': item.itemName,
          'Quantity': item.quantity,
          'Gross': item.totalGross,
          'Net': item.totalNet,
          'Discount': item.totalDiscount,
        };
      } else {
        return {
          'Order #': item.orderNumber,
          'Item': item.itemName,
          'Gross': parseFloat(item.grossSale) || 0,
          'Net': parseFloat(item.netSale) || 0,
          'Discount': parseFloat(item.discountAmount) || 0,
          'Refund': parseFloat(item.refundAmount) || 0,
        };
      }
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Details');
    XLSX.writeFile(wb, `sales-details-${storeId}-${businessDate}.xlsx`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getCategoryIcon()}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            {getCategoryLabel()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            Store {storeId} - {businessDate} - GL {glAccount}
          </Typography>
        </Box>
        <Chip
          label={`${totals.count} sold`}
          size="small"
          variant="outlined"
        />
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && (
          <Alert severity="info">
            No sales found for this category.
          </Alert>
        )}

        {!loading && !error && details.length > 0 && (
          <>
            {/* View mode toggle */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    setViewMode(newMode);
                    // Set default sort based on view mode
                    if (newMode === 'grouped') {
                      setOrderBy('totalGross');
                      setOrder('desc');
                    } else {
                      setOrderBy('orderNumber');
                      setOrder('asc');
                    }
                  }
                }}
                size="small"
              >
                <ToggleButton value="grouped">
                  <CategoryIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  By Item
                </ToggleButton>
                <ToggleButton value="individual">
                  <ViewListIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  All Orders
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TableContainer sx={{ maxHeight: 400, userSelect: 'text' }} onCopy={handleCopy}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {viewMode === 'individual' && showOrderGuid && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        Order GUID
                      </TableCell>
                    )}
                    {viewMode === 'individual' && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'orderNumber'}
                          direction={orderBy === 'orderNumber' ? order : 'asc'}
                          onClick={() => handleRequestSort('orderNumber')}
                        >
                          Order #
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'itemName'}
                        direction={orderBy === 'itemName' ? order : 'asc'}
                        onClick={() => handleRequestSort('itemName')}
                      >
                        Item
                      </TableSortLabel>
                    </TableCell>
                    {viewMode === 'grouped' && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="center">
                        <TableSortLabel
                          active={orderBy === 'quantity'}
                          direction={orderBy === 'quantity' ? order : 'asc'}
                          onClick={() => handleRequestSort('quantity')}
                        >
                          Qty
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'grossSale' || orderBy === 'totalGross'}
                        direction={(orderBy === 'grossSale' || orderBy === 'totalGross') ? order : 'asc'}
                        onClick={() => handleRequestSort(viewMode === 'grouped' ? 'totalGross' : 'grossSale')}
                      >
                        Gross
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'netSale' || orderBy === 'totalNet'}
                        direction={(orderBy === 'netSale' || orderBy === 'totalNet') ? order : 'asc'}
                        onClick={() => handleRequestSort(viewMode === 'grouped' ? 'totalNet' : 'netSale')}
                      >
                        Net
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'discountAmount' || orderBy === 'totalDiscount'}
                        direction={(orderBy === 'discountAmount' || orderBy === 'totalDiscount') ? order : 'asc'}
                        onClick={() => handleRequestSort(viewMode === 'grouped' ? 'totalDiscount' : 'discountAmount')}
                      >
                        Discount
                      </TableSortLabel>
                    </TableCell>
                    {viewMode === 'individual' && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                        <TableSortLabel
                          active={orderBy === 'refundAmount'}
                          direction={orderBy === 'refundAmount' ? order : 'asc'}
                          onClick={() => handleRequestSort('refundAmount')}
                        >
                          Refund
                        </TableSortLabel>
                      </TableCell>
                    )}
                  </TableRow>
                  <TableRow>
                    {viewMode === 'individual' && showOrderGuid && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={columnFilters.orderGuid || ''}
                          onChange={(e) => handleColumnFilterChange('orderGuid', e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters.orderGuid && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => clearColumnFilter('orderGuid')}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: '100%' }}
                        />
                      </TableCell>
                    )}
                    {viewMode === 'individual' && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={columnFilters.orderNumber || ''}
                          onChange={(e) => handleColumnFilterChange('orderNumber', e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters.orderNumber && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => clearColumnFilter('orderNumber')}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: '100%' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.itemName || ''}
                        onChange={(e) => handleColumnFilterChange('itemName', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.itemName && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('itemName')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    {viewMode === 'grouped' && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={columnFilters.quantity || ''}
                          onChange={(e) => handleColumnFilterChange('quantity', e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters.quantity && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => clearColumnFilter('quantity')}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: '100%' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.grossSale || ''}
                        onChange={(e) => handleColumnFilterChange('grossSale', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.grossSale && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('grossSale')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.netSale || ''}
                        onChange={(e) => handleColumnFilterChange('netSale', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.netSale && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('netSale')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.discountAmount || ''}
                        onChange={(e) => handleColumnFilterChange('discountAmount', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.discountAmount && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('discountAmount')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    {viewMode === 'individual' && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={columnFilters.refundAmount || ''}
                          onChange={(e) => handleColumnFilterChange('refundAmount', e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters.refundAmount && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => clearColumnFilter('refundAmount')}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: '100%' }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewMode === 'grouped'
                    ? sortedGroupedData.map((item, index) => (
                        <TableRow
                          key={`${item.itemName}-${index}`}
                          sx={{
                            '&:hover': { backgroundColor: '#f5f5f5' },
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                          }}
                        >
                          <TableCell>{item.itemName}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item.quantity}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ minWidth: 40 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {formatCurrency(item.totalGross)}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(item.totalNet)}</TableCell>
                          <TableCell align="right" sx={{ color: item.totalDiscount > 0 ? 'warning.main' : 'text.secondary' }}>
                            {formatCurrency(item.totalDiscount)}
                          </TableCell>
                        </TableRow>
                      ))
                    : sortedDetails.map((detail, index) => (
                        <TableRow
                          key={`${detail.orderGuid}-${detail.itemId}-${index}`}
                          sx={{
                            '&:hover': { backgroundColor: '#f5f5f5' },
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                          }}
                        >
                          {showOrderGuid && (
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {detail.orderGuid || '-'}
                            </TableCell>
                          )}
                          <TableCell>{detail.orderNumber || '-'}</TableCell>
                          <TableCell>{detail.itemName || '-'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {formatCurrency(detail.grossSale)}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(detail.netSale)}</TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(detail.discountAmount) > 0 ? 'warning.main' : 'text.secondary' }}>
                            {formatCurrency(detail.discountAmount)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(detail.refundAmount) > 0 ? 'error.main' : 'text.secondary' }}>
                            {formatCurrency(detail.refundAmount)}
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {viewMode === 'grouped'
              ? hasActiveFilters
                ? `${uniqueItemCount} of ${totalUniqueItemCount} unique items`
                : `${uniqueItemCount} unique items`
              : hasActiveFilters
              ? `${totals.count} of ${details.length} items sold`
              : `${totals.count} items sold`}
          </Typography>
          {viewMode === 'individual' && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showOrderGuid}
                  onChange={(e) => setShowOrderGuid(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Show Order GUID</Typography>}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Total Gross
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatCurrency(totals.grossSale)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Total Net
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatCurrency(totals.netSale)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Discounts
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: 'warning.main' }}>
              {formatCurrency(totals.discount)}
            </Typography>
          </Box>
          {viewMode === 'individual' && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                Refunds
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, color: 'error.main' }}>
                {formatCurrency(totals.refund)}
              </Typography>
            </Box>
          )}
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={details.length === 0}
          >
            Export
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Box>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default SalesDetailsDialog;
